import { Can, useCan } from '@/components/can/Can';
import { Tabs } from '@/components/tabs/Tabs';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { UserCogIcon } from '@/icons';

import { useDialogFormStore } from '@/components/dialog/form.store';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import {
  CustomerPointSettingListResponse,
  PointConfigurationData,
} from '@/services/pointConfiguration/pointConfiguration.types';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { Button } from 'ui/button';
import AssignPointsFormContainer from '../point-setup-forms/AssignPointsFormContainer';
import { ManageReferralValidityContainer } from '../point-setup-forms/customer/ManageReferralValidityContainer';
import { CustomerPointsConfigurationTable } from './customerTable/table';
import { PointsConfigurationTable } from './table';

const activeTabContent = {
  AFFILIATE: 'affiliate',
  CUSTOMER: 'customer',
};
const PointsConfiguration = ({
  data,
  isLoading,
  pagination,
}: {
  data: PointConfigurationData[] | CustomerPointSettingListResponse[];
  isLoading: boolean;
  pagination: Pagination;
}) => {
  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const canAccessAffiliatePoint = useCan({ allOf: [PERMS.point.management.list] });
  const canAccessCustomerPoint = useCan({ allOf: [PERMS.point.management.customer.list] });
  const canManageReferralValidity = useCan({ allOf: [PERMS.point.management.customer.referralValidity] });
  const tabItems = [
    {
      label: t('userManagement.rolesAndPrivileges.tabs.affiliate'),
      value: 'affiliate',
      content: (
        <PointsConfigurationTable
          data={data as PointConfigurationData[]}
          isLoading={isLoading}
          pagination={pagination}
        />
      ),
      disabled: !canAccessAffiliatePoint,
    },
    {
      label: t('userManagement.rolesAndPrivileges.tabs.club'),
      value: 'club',
      content: (
        <PointsConfigurationTable
          data={data as PointConfigurationData[]}
          isLoading={isLoading}
          pagination={pagination}
        />
      ),
      disabled: true,
    },
    {
      label: t('userManagement.rolesAndPrivileges.tabs.customer'),
      value: 'customer',
      content: (
        <CustomerPointsConfigurationTable
          data={data as CustomerPointSettingListResponse[]}
          isLoading={isLoading}
          pagination={pagination}
        />
      ),
      disabled: !canAccessCustomerPoint,
    },
  ];
  const defaultTab = canAccessAffiliatePoint ? 'affiliate' : canAccessCustomerPoint ? 'customer' : '';
  const [activeTabQuery, setActiveTab] = useQueryState('activeTab', parseAsString);
  const activeTab = activeTabQuery ?? defaultTab;
  const [, setPage] = useQueryState('page', parseAsInteger.withDefault(0));
  const [, setCreatedDate] = useQueryState('createdDate', parseAsString);
  const [, setSearchQuery] = useQueryState('q', parseAsString);
  const [, setStatus] = useQueryState('status', parseAsString);

  const onTabChange = (tab: string): void => {
    void Promise.all([setActiveTab(tab), setPage(0), setCreatedDate(null), setSearchQuery(null), setStatus(null)]);
  };

  return (
    <>
      <div className="relative">
        {activeTab === activeTabContent.AFFILIATE ? (
          <Can rule={{ allOf: [PERMS.point.management.assign] }}>
            <Button
              className="absolute right-0 -top-[66px] border-foreground"
              variant="outline"
              type="button"
              onClick={() =>
                onOpen({
                  modalTitle: 'pointManagement.page.form.managePoints.title',
                  title: t('pointManagement.page.form.managePoints.title'),
                  okText: t('common.page.button.submit'),
                  cancelText: t('common.page.button.cancel'),
                  size: 'lg',
                  component: <AssignPointsFormContainer />,
                })
              }
            >
              <UserCogIcon />
              <Translation id="pointManagement.pointsConfiguration.page.assignPoints" />
            </Button>
          </Can>
        ) : (
          canManageReferralValidity && (
            <Button
              className="absolute right-0 -top-[66px] border-foreground"
              variant="outline"
              type="button"
              onClick={() =>
                onOpen({
                  modalTitle: 'pointManagement.pointsConfiguration.customer.referralValidityForm.title',
                  title: t('pointManagement.pointsConfiguration.customer.referralValidityForm.title'),
                  okText: t('common.page.button.submit'),
                  size: 'p0',
                  okButtonDisabled: true,
                  component: <ManageReferralValidityContainer />,
                })
              }
            >
              <UserCogIcon />
              <Translation id="pointManagement.pointsConfiguration.customer.button.referralValidity" />
            </Button>
          )
        )}
      </div>
      <Tabs
        className="rounded-lg shadow bg-muted"
        listClassName="bg-muted"
        triggerClassName="bg-muted rounded-t-[8px] px-6 py-3"
        defaultActive={activeTab}
        value={activeTab}
        items={tabItems}
        onChange={onTabChange}
      />
    </>
  );
};

export default PointsConfiguration;
